import { Button } from "@groot/ui/button";
import { PageHeader } from "@groot/shell/components/layout";
import { Page } from "@/app/shared/components/page";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@groot/ui/tabs";
import { useDetailPage } from "@/hooks";
import { formatBytes, formatDisplayDate } from "@/lib/utils";
import { apiClient } from "@/services/api";
import { ArrowLeft, CheckCircle, ExternalLink, Paperclip, Trash2 } from "lucide-react";
import { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

export function InboundEmailDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const fetchEmail = useCallback(
    (id: string) => apiClient.getInboundEmailById(Number.parseInt(id, 10)),
    [],
  );

  const deleteEmail = useCallback(
    (id: string) => apiClient.deleteInboundEmail(Number.parseInt(id, 10)),
    [],
  );

  const {
    data: email,
    loading,
    error,
    handleDelete,
  } = useDetailPage({
    fetchFn: fetchEmail,
    deleteFn: deleteEmail,
    id,
    redirectPath: "/inbound-emails",
    resourceName: "email",
  });

  if (loading) {
    return (
      <Page maxWidth="5xl">
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  if (!email) {
    return (
      <Page maxWidth="5xl">
        <Button variant="ghost" onClick={() => navigate("/inbound-emails")} className="mb-4 -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Emails
        </Button>
        <div className="text-center py-20">
          <p className="text-muted-foreground">{error || "Email not found"}</p>
        </div>
      </Page>
    );
  }

  return (
    <Page maxWidth="5xl">
      {/* Header */}
      <Button variant="ghost" onClick={() => navigate("/inbound-emails")} className="mb-4 -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Emails
      </Button>
      <PageHeader
        title={email.subject || "(No subject)"}
        description={formatDisplayDate(email.date)}
        actions={
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="size-4 mr-2" />
            Delete
          </Button>
        }
        className="mb-8"
      />

      <div className="space-y-8">
        {/* Email Metadata */}
        <div>
          <h2 className="text-sm font-medium text-foreground mb-4">Details</h2>
          <dl className="space-y-3">
            <div className="flex items-start">
              <dt className="text-sm text-muted-foreground w-20">From:</dt>
              <dd className="text-sm text-foreground">{email.from || "Unknown"}</dd>
            </div>
            <div className="flex items-start">
              <dt className="text-sm text-muted-foreground w-20">To:</dt>
              <dd className="text-sm text-foreground">{email.to || "Unknown"}</dd>
            </div>
            <div className="flex items-start">
              <dt className="text-sm text-muted-foreground w-20">Date:</dt>
              <dd className="text-sm text-foreground">{formatDisplayDate(email.date)}</dd>
            </div>
            {email.messageId && (
              <div className="flex items-start">
                <dt className="text-sm text-muted-foreground w-20">ID:</dt>
                <dd className="text-xs text-muted-foreground font-mono break-all flex-1">
                  {email.messageId}
                </dd>
              </div>
            )}
            <div className="flex items-start">
              <dt className="text-sm text-muted-foreground w-20">Source:</dt>
              <dd className="text-sm text-muted-foreground">{email.source || "unknown"}</dd>
            </div>
            {email.transactions && email.transactions.length > 0 && (
              <div className="flex items-start">
                <dt className="text-sm text-muted-foreground w-20">Status:</dt>
                <dd className="text-sm text-success">
                  <CheckCircle className="size-3 mr-1 inline" />
                  {email.transactions.length} transaction
                  {email.transactions.length > 1 ? "s" : ""} created
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Email Content */}
        {email.body || email.htmlBody ? (
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-medium text-foreground mb-4">Content</h2>
            <Tabs defaultValue={email.htmlBody ? "html" : "text"} className="w-full">
              {email.body && email.htmlBody && (
                <TabsList className="mb-4">
                  <TabsTrigger value="text">Plain Text</TabsTrigger>
                  <TabsTrigger value="html">HTML</TabsTrigger>
                </TabsList>
              )}

              {email.body && (
                <TabsContent value="text">
                  <div className="border border-border rounded-lg p-6 bg-muted">
                    <pre className="whitespace-pre-wrap text-sm leading-relaxed text-foreground font-sans">
                      {email.body}
                    </pre>
                  </div>
                </TabsContent>
              )}

              {email.htmlBody && (
                <TabsContent value="html">
                  <div className="border border-border rounded-lg p-6 bg-background">
                    <div
                      className="prose prose-sm max-w-none"
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: email content is sanitized on server
                      dangerouslySetInnerHTML={{ __html: email.htmlBody }}
                    />
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>
        ) : (
          <div className="pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground">No email content available</p>
          </div>
        )}

        {/* Attachments */}
        {email.attachments && email.attachments.length > 0 && (
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-medium text-foreground mb-4">
              Attachments ({email.attachments.length})
            </h2>
            <div className="border border-border rounded-lg divide-y divide-border">
              {email.attachments.map((attachment, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: attachments have no unique id
                  key={index}
                  className="p-4 flex items-center justify-between hover:bg-muted"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Paperclip className="size-5 text-muted-foreground/70 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {attachment.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {attachment.contentType} • {formatBytes(attachment.size)}
                      </div>
                      {attachment.error && (
                        <div className="text-xs text-destructive mt-1">{attachment.error}</div>
                      )}
                    </div>
                  </div>
                  {!attachment.error && (
                    <Button variant="ghost" size="sm" asChild className="flex-shrink-0">
                      <a
                        href={`/api/v1/inbound-emails/${email.id}/attachments/${index}/download`}
                        download={attachment.name}
                      >
                        <ExternalLink className="size-4 mr-2" />
                        Download
                      </a>
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Related Transactions */}
        {email.transactions && email.transactions.length > 0 && (
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-medium text-foreground mb-4">
              Related Transactions ({email.transactions.length})
            </h2>
            <div className="border border-border rounded-lg divide-y divide-border">
              {email.transactions.map((txn) => (
                <div key={txn.id} className="p-4 flex items-center justify-between hover:bg-muted">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      ₹{txn.amount.toFixed(2)}
                      <span className="text-xs text-muted-foreground ml-2">{txn.type}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {txn.description || "No description"}
                    </div>
                  </div>
                  <Button asChild variant="ghost" size="sm">
                    <Link to={`/transactions?id=${txn.id}`}>
                      <ExternalLink className="size-4 mr-2" />
                      View
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Metadata */}
        {email.meta && Object.keys(email.meta).length > 0 && (
          <div className="pt-6 border-t border-border">
            <h2 className="text-sm font-medium text-foreground mb-4">Additional Metadata</h2>
            <dl className="space-y-3">
              {Object.entries(email.meta).map(([key, value]) => (
                <div key={key} className="flex items-start">
                  <dt className="text-sm text-muted-foreground min-w-[150px]">{key}:</dt>
                  <dd className="text-xs text-muted-foreground font-mono break-all flex-1">
                    {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </Page>
  );
}
